import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Dimensions, Platform } from 'react-native';
import { useTheme } from '@/theme';

type TutorialHighlightProps = {
  targetRef?: React.RefObject<View>;
  targetLayout?: { x: number; y: number; width: number; height: number };
  visible: boolean;
  children?: React.ReactNode;
};

export function TutorialHighlight({
  targetRef,
  targetLayout,
  visible,
  children,
}: TutorialHighlightProps) {
  const theme = useTheme();
  const [highlightLayout, setHighlightLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

  useEffect(() => {
    if (visible) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, fadeAnim]);

  useEffect(() => {
    if (!visible) return;

    const measureTarget = () => {
      if (targetLayout) {
        setHighlightLayout(targetLayout);
        return;
      }

      if (targetRef?.current) {
        targetRef.current.measureInWindow((x, y, width, height) => {
          setHighlightLayout({ x, y, width, height });
        });
      }
    };

    // Small delay to ensure layout is complete
    const timeout = setTimeout(measureTarget, 100);
    return () => clearTimeout(timeout);
  }, [visible, targetRef, targetLayout]);

  if (!visible || !highlightLayout) {
    return null;
  }

  const { x, y, width, height } = highlightLayout;
  const highlightRadius = 8;
  const padding = 4; // Padding around the highlighted element
  // Clamp padding at screen edges so the highlight never renders off-screen (common for top-right buttons).
  const padLeft = Math.min(padding, Math.max(0, x));
  const padTop = Math.min(padding, Math.max(0, y));
  const padRight = Math.min(padding, Math.max(0, screenWidth - (x + width)));
  const padBottom = Math.min(padding, Math.max(0, screenHeight - (y + height)));

  return (
    <Animated.View
      style={[
        styles.overlay,
        {
          opacity: fadeAnim,
        },
      ]}
      pointerEvents="box-none"
    >
      {/* Dimmed background with cutout */}
      <View style={styles.overlayContent}>
        {/* Top dimmed area */}
        {y > 0 && (
          <View
            style={[
              styles.dimmedArea,
              {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                height: Math.max(0, y - padTop),
              },
            ]}
          />
        )}

        {/* Middle row: left dimmed, highlight, right dimmed */}
        <View style={styles.middleRow}>
          {/* Left dimmed */}
          {x > 0 && (
            <View
              style={[
                styles.dimmedArea,
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  width: Math.max(0, x - padLeft),
                },
              ]}
            />
          )}

          {/* Highlighted area */}
          <View
            style={[
              styles.highlight,
              {
                width: width + padLeft + padRight,
                height: height + padTop + padBottom,
                borderRadius: highlightRadius,
                borderWidth: 2,
                borderColor: theme.colors.primary,
                backgroundColor: 'transparent',
                ...Platform.select({
                  ios: {
                    shadowColor: theme.colors.primary,
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 0.5,
                    shadowRadius: 8,
                  },
                  android: {
                    elevation: 8,
                  },
                }),
              },
            ]}
          />

          {/* Right dimmed */}
          {x + width < screenWidth && (
            <View
              style={[
                styles.dimmedArea,
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  width: Math.max(0, screenWidth - (x + width + padRight)),
                },
              ]}
            />
          )}
        </View>

        {/* Bottom dimmed area */}
        {y + height < screenHeight && (
          <View
            style={[
              styles.dimmedArea,
              {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                height: Math.max(0, screenHeight - (y + height + padBottom)),
              },
            ]}
          />
        )}
      </View>

      {/* Children (tooltip) rendered on top */}
      {children}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  },
  overlayContent: {
    flex: 1,
  },
  middleRow: {
    flexDirection: 'row',
  },
  dimmedArea: {
    flexShrink: 0,
  },
  highlight: {
    flexShrink: 0,
  },
});

